import type { Schemas } from "@artisan/api-client";
import { Button, Card, cn, formatMNT, Select } from "@artisan/ui";
import { MapPin, Minus, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Navigate, useParams } from "react-router";

import { CatalogBrowser, type Hit, hitIcon } from "../components/catalog-browser";
import { Screen } from "../components/screen";
import { api, describeError, unwrap } from "../lib/api";
import { projectTitle } from "../project/actions";
import { type ChoiceSlot, type PickedProduct, type Project, unitLabel } from "../project/model";
import { updateProject, useProject } from "../project/store";

const SLOTS: { key: ChoiceSlot; label: string; hint: string }[] = [
  { key: "carcass", label: "Корпус", hint: "18 мм цагаан ЛДСП" },
  { key: "front", label: "Нүүр хавтан", hint: "Фасадын материал, өнгө" },
  { key: "handle", label: "Бариул", hint: "Профиль, товч" },
  { key: "worktop", label: "Тавцан", hint: "38 мм чулуун хээ" },
  { key: "style", label: "Хэв маяг", hint: "Скандинав, модерн…" },
];

function slotFor(hit: Hit): ChoiceSlot | null {
  const c = hit.category.render_class;
  if (c === "board") return "carcass";
  if (c === "front") return "front";
  if (c === "worktop") return "worktop";
  if (hit.category.slug.startsWith("hardware/handles")) return "handle";
  return null;
}

/** The choices without one slot (a copy, so no dynamic delete). */
function without(choices: Project["choices"], slot: ChoiceSlot): Project["choices"] {
  return Object.fromEntries(Object.entries(choices).filter(([k]) => k !== slot));
}

function withRow(p: Project, i: number, fn: (row: PickedProduct) => void) {
  const row = p.picks[i];
  if (row) fn(row);
}

function choiceText(p: PickedProduct): string {
  return `${p.name}${p.sku ? ` (${p.sku})` : ""}`.slice(0, 200);
}

type Offer = Schemas["NearbyOfferOut"];

function NearbyOffers({ project }: { project: Project }) {
  const [offers, setOffers] = useState<Offer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const hasPlace = project.site.lat !== undefined && project.site.lng !== undefined;
  async function load() {
    const { lat, lng } = project.site;
    if (lat === undefined || lng === undefined || !project.picks.length) return;
    setBusy(true);
    try {
      const r = await unwrap(
        api().POST("/v1/catalog/offers/nearby", {
          body: { product_ids: project.picks.map((p) => p.product_id), lat, lng, radius_km: 30, delivers_only: false },
        }),
      );
      setOffers(r.items);
      setError(null);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }
  const best = new Map<string, Offer>();
  for (const o of offers ?? []) {
    const cur = best.get(o.product_id);
    if (!cur || o.price_mnt < cur.price_mnt) best.set(o.product_id, o);
  }
  return (
    <Card className="space-y-3 p-5">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-semibold">
          <MapPin className="size-4 text-muted-foreground" aria-hidden /> Ойрын нийлүүлэгч
        </h2>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={!hasPlace || !project.picks.length || busy}>
          {busy ? "Хайж байна…" : "30 км-т хайх"}
        </Button>
      </div>
      {!hasPlace ? <p className="text-sm text-muted-foreground">Захиалагч хэсэгт объектын байршлыг авна уу.</p> : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {offers && offers.length === 0 ? <p className="text-sm text-muted-foreground">Ойролцоо санал олдсонгүй.</p> : null}
      {offers && offers.length ? (
        <ul className="divide-y">
          {project.picks.map((p) => {
            const o = best.get(p.product_id);
            return (
              <li key={p.product_id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate">{p.name}</span>
                {o ? (
                  <span className="text-right">
                    <span className="block font-semibold tabular">{formatMNT(o.price_mnt * p.qty)}</span>
                    <span className="block text-xs text-muted-foreground">
                      {o.branch_name} · {(o.distance_m / 1000).toFixed(1)} км{o.lead_time_days ? ` · ${o.lead_time_days} хоног` : ""}
                    </span>
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">санал алга</span>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </Card>
  );
}

export function DesignScreen() {
  const { id } = useParams();
  const project = useProject(id);
  if (!project) return <Navigate to="/" replace />;

  const edit = (fn: (p: Project) => void) => void updateProject(project.id, fn);
  const total = project.picks.reduce((s, p) => s + (p.min_price_mnt ?? 0) * p.qty, 0);

  function pick(hit: Hit) {
    edit((p) => {
      const existing = p.picks.findIndex((x) => x.product_id === hit.id);
      if (existing >= 0) {
        const [removed] = p.picks.splice(existing, 1);
        if (removed?.slot && p.choices[removed.slot] === choiceText(removed)) p.choices = without(p.choices, removed.slot);
        return;
      }
      const slot = slotFor(hit);
      const item: PickedProduct = {
        product_id: hit.id,
        name: hit.name_mn,
        sku: hit.sku,
        slot: slot && !p.picks.some((x) => x.slot === slot) ? slot : null,
        unit: hit.unit,
        qty: 1,
        min_price_mnt: hit.min_price_mnt,
        category: hit.category.name_mn,
      };
      p.picks.push(item);
      if (item.slot && !p.choices[item.slot]) p.choices[item.slot] = choiceText(item);
    });
  }

  return (
    <Screen back={`/p/${project.id}`} title="Загвар, материал" subtitle={projectTitle(project)}>
      <div className="grid grid-cols-[400px_1fr] gap-6">
        <div className="space-y-5">
          <Card className="space-y-3 p-5">
            <h2 className="font-semibold">Үйлдвэрт очих сонголт</h2>
            {SLOTS.map((s) => (
              <label key={s.key} className="grid gap-1 text-xs font-medium text-muted-foreground">
                {s.label}
                <input
                  className="h-11 rounded-lg border bg-card px-3 text-[15px] text-foreground outline-none focus:shadow-ring"
                  placeholder={s.hint}
                  defaultValue={project.choices[s.key] ?? ""}
                  key={project.choices[s.key] ?? ""}
                  maxLength={200}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (project.choices[s.key] ?? "")) edit((p) => (v ? (p.choices[s.key] = v) : (p.choices = without(p.choices, s.key))));
                  }}
                />
              </label>
            ))}
          </Card>
          <Card className="space-y-3 p-5">
            <div className="flex items-baseline justify-between">
              <h2 className="font-semibold">Сонгосон бараа · {project.picks.length}</h2>
              <span className="font-semibold tabular">{formatMNT(total)}</span>
            </div>
            {project.picks.length === 0 ? <p className="text-sm text-muted-foreground">Баруун талын каталогоос нэмнэ үү.</p> : null}
            <ul className="space-y-2">
              {project.picks.map((p, i) => {
                const Icon = hitIcon(null);
                return (
                  <li key={p.product_id} className="rounded-lg border p-2.5">
                    <div className="flex items-start gap-2">
                      <Icon className="mt-0.5 size-4 text-muted-foreground" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{p.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {p.category}
                          {p.min_price_mnt !== null ? ` · ${formatMNT(p.min_price_mnt)}/${unitLabel(p.unit)}` : ""}
                        </p>
                      </div>
                      <Button variant="ghost" size="icon-sm" aria-label="Хасах" onClick={() => edit((x) => void x.picks.splice(i, 1))}>
                        <Trash2 aria-hidden />
                      </Button>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <Select
                        aria-label="Хаана хэрэглэх"
                        value={p.slot ?? ""}
                        onChange={(e) =>
                          edit((x) => {
                            const slot = (e.target.value || null) as ChoiceSlot | null;
                            const row = x.picks[i];
                            if (!row) return;
                            row.slot = slot;
                            if (slot) x.choices[slot] = choiceText(row);
                          })
                        }
                      >
                        <option value="">Нэмэлт бараа</option>
                        {SLOTS.map((s) => (
                          <option key={s.key} value={s.key}>
                            {s.label}
                          </option>
                        ))}
                      </Select>
                      <div className="ml-auto flex items-center rounded-lg border">
                        <button type="button" className="grid size-9 place-items-center" aria-label="Хасах" onClick={() => edit((x) => withRow(x, i, (row) => (row.qty = Math.max(1, row.qty - 1))))}>
                          <Minus className="size-4" aria-hidden />
                        </button>
                        <span className={cn("w-8 text-center text-sm tabular")}>{p.qty}</span>
                        <button type="button" className="grid size-9 place-items-center" aria-label="Нэмэх" onClick={() => edit((x) => withRow(x, i, (row) => (row.qty += 1)))}>
                          <Plus className="size-4" aria-hidden />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Card>
          <NearbyOffers project={project} />
        </div>
        <CatalogBrowser onPick={pick} picked={project.picks.map((p) => p.product_id)} columns={2} />
      </div>
    </Screen>
  );
}
